import type { Action } from 'svelte/action';

export type TooltipTone = 'default' | 'error' | 'success';
export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

export interface TooltipOptions {
    text: string;
    placement?: TooltipPlacement;
    tone?: TooltipTone;
}

export type TooltipParameter = TooltipOptions | string | null | undefined;

const tooltipId = 'app-tooltip';
const showDelay = 120;
const gap = 6;
const edgePadding = 8;

let popover: HTMLDivElement | null = null;
let activeTrigger: HTMLElement | null = null;
let pendingTrigger: HTMLElement | null = null;
let previousDescribedBy: string | null = null;
let showTimer: ReturnType<typeof setTimeout> | undefined;

function resolve(parameter: TooltipParameter): TooltipOptions {
    if (typeof parameter === 'string') return { text: parameter };
    return parameter ?? { text: '' };
}

function getPopover(): HTMLDivElement | null {
    if (typeof document === 'undefined') return null;
    if (popover?.isConnected) return popover;

    const element = document.createElement('div');
    element.id = tooltipId;
    element.className =
        'pointer-events-none fixed inset-auto m-0 w-max max-w-80 rounded-sm border-0 bg-foreground px-2 py-1 text-center text-sm text-background opacity-0 transition-opacity duration-150 ease-out motion-reduce:transition-none data-visible:opacity-100 data-[tone=error]:bg-destructive data-[tone=success]:bg-success';
    element.setAttribute('popover', 'manual');
    element.setAttribute('role', 'tooltip');
    document.body.appendChild(element);

    popover = element;
    return element;
}

function position(element: HTMLDivElement, trigger: HTMLElement, placement: TooltipPlacement) {
    const anchor = trigger.getBoundingClientRect();
    const { width, height } = element.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let side = placement;
    if (side === 'top' && anchor.top - height - gap < edgePadding) side = 'bottom';
    else if (side === 'bottom' && anchor.bottom + height + gap > viewportHeight - edgePadding) side = 'top';
    else if (side === 'left' && anchor.left - width - gap < edgePadding) side = 'right';
    else if (side === 'right' && anchor.right + width + gap > viewportWidth - edgePadding) side = 'left';

    let left: number;
    let top: number;

    if (side === 'top' || side === 'bottom') {
        left = anchor.left + anchor.width / 2 - width / 2;
        top = side === 'top' ? anchor.top - height - gap : anchor.bottom + gap;
    } else {
        left = side === 'left' ? anchor.left - width - gap : anchor.right + gap;
        top = anchor.top + anchor.height / 2 - height / 2;
    }

    left = Math.min(Math.max(left, edgePadding), Math.max(edgePadding, viewportWidth - width - edgePadding));
    top = Math.min(Math.max(top, edgePadding), Math.max(edgePadding, viewportHeight - height - edgePadding));

    element.style.left = `${Math.round(left)}px`;
    element.style.top = `${Math.round(top)}px`;
}

function hide() {
    clearTimeout(showTimer);
    showTimer = undefined;
    pendingTrigger = null;

    if (activeTrigger) {
        if (previousDescribedBy === null) activeTrigger.removeAttribute('aria-describedby');
        else activeTrigger.setAttribute('aria-describedby', previousDescribedBy);
    }

    activeTrigger = null;
    previousDescribedBy = null;

    document.removeEventListener('keydown', onKeydown, true);
    document.removeEventListener('scroll', hide, true);

    if (!popover?.isConnected) return;

    popover.removeAttribute('data-visible');
    if (popover.matches(':popover-open')) popover.hidePopover();
}

function onKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') hide();
}

function isKeyboardFocus(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) return false;

    try {
        return target.matches(':focus-visible');
    } catch {
        return true;
    }
}

function show(trigger: HTMLElement, options: TooltipOptions) {
    const element = getPopover();
    if (!element || typeof element.showPopover !== 'function') return;

    hide();

    // The delay timer closes over a mutable options binding, so the text can be blanked after it is armed.
    if (!options.text) return;

    element.textContent = options.text;
    element.dataset.tone = options.tone ?? 'default';
    element.style.left = '0px';
    element.style.top = '0px';

    try {
        element.showPopover();
    } catch {
        return;
    }

    position(element, trigger, options.placement ?? 'top');

    activeTrigger = trigger;
    previousDescribedBy = trigger.getAttribute('aria-describedby');
    trigger.setAttribute('aria-describedby', tooltipId);

    requestAnimationFrame(() => {
        if (activeTrigger === trigger) element.setAttribute('data-visible', '');
    });

    document.addEventListener('keydown', onKeydown, true);
    document.addEventListener('scroll', hide, { capture: true, passive: true });
}

export const tooltip: Action<HTMLElement, TooltipParameter> = (node, parameter) => {
    let options = resolve(parameter);

    const open = () => {
        if (!options.text) return;

        clearTimeout(showTimer);
        pendingTrigger = node;
        showTimer = setTimeout(() => show(node, options), showDelay);
    };

    const close = () => {
        if (activeTrigger === node || pendingTrigger === node) hide();
    };

    // Programmatic focus restore (a closing dialog, for one) would otherwise strand a bubble
    // the pointer never entered and so can never leave.
    const openOnFocus = (event: FocusEvent) => {
        if (isKeyboardFocus(event.target)) open();
    };

    node.addEventListener('pointerenter', open);
    node.addEventListener('pointerleave', close);
    node.addEventListener('focusin', openOnFocus);
    node.addEventListener('focusout', close);

    return {
        update(next: TooltipParameter) {
            options = resolve(next);

            if (!options.text) {
                close();
                return;
            }

            if (activeTrigger === node) show(node, options);
        },
        destroy() {
            close();
            node.removeEventListener('pointerenter', open);
            node.removeEventListener('pointerleave', close);
            node.removeEventListener('focusin', openOnFocus);
            node.removeEventListener('focusout', close);
        }
    };
};
